/**
 * DragDropContext for UI-Model-1
 *
 * Provides drag-and-drop functionality using @hello-pangea/dnd.
 * Handles three scenarios:
 * 1. Same container reorder → REORDER_ITEMS command
 * 2. Cross-container move → MOVE_ITEM command
 * 3. Library drop → ADD_ITEM command with insertIndex
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import {
  DragDropContext as RBDDragDropContext,
  DropResult,
  DragStart,
  DragUpdate,
  ResponderProvided,
} from '@hello-pangea/dnd';
import { useUIModelState } from './UIModelStateContext';
import {
  createReorderItemsCommand,
  createMoveItemCommand,
} from '../types/command.types';
import { ItemAPI, isItemAPI } from '../types/state.types';
import * as selectors from '../selectors';
import { useModule } from './ModuleContext';
import { canAddSubmodelItemToContainer } from '../container-management/validators/submodelLockValidator';
import { toast } from 'Components/DesignSystem/Toast/useToast';

// ============================================================================
// Types
// ============================================================================

export interface DragState {
  isDragging: boolean;
  sourceDroppableId: string | null;
  sourceIndex: number;
  draggableId: string | null;
  draggedItemType: 'item' | 'library' | null;
}

/**
 * Position information for the drop indicator line
 */
export interface DropIndicatorPosition {
  top: number;
  left: number;
  width: number;
}

export interface DropIndicatorState {
  containerId: string | null;
  index: number;
  isVisible: boolean;
  /** Position for absolute positioning of the indicator */
  position: DropIndicatorPosition | null;
}

/**
 * State for container highlight during drag operations
 */
export interface ContainerHighlightState {
  containerId: string | null;
  isActive: boolean;
}

interface DragDropContextValue {
  dragState: DragState;
  dropIndicator: DropIndicatorState;
  setDropIndicator: (state: DropIndicatorState) => void;
  containerHighlight: ContainerHighlightState;
  setContainerHighlight: (state: ContainerHighlightState) => void;
}

// ============================================================================
// Context
// ============================================================================

const DragDropContextInternal = createContext<DragDropContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface DragDropProviderProps {
  children: ReactNode;
}

export const DragDropProvider = ({
  children,
}: DragDropProviderProps) => {
  const { state, dispatch, getContainer } = useUIModelState();
  const { getSubmodelInstanceLimits } = useModule();

  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    sourceDroppableId: null,
    sourceIndex: 0,
    draggableId: null,
    draggedItemType: null,
  });

  const [dropIndicator, setDropIndicator] = useState<DropIndicatorState>({
    containerId: null,
    index: 0,
    isVisible: false,
    position: null,
  });

  const [containerHighlight, setContainerHighlight] = useState<ContainerHighlightState>({
    containerId: null,
    isActive: false,
  });

  /**
   * Get the canvas ID for a container
   */
  const getCanvasIdForContainer = useCallback((containerId: string): string | null => {
    const { path } = getContainer(containerId);
    return path?.canvasId || null;
  }, [getContainer]);

  /**
   * Get items for a container (only actual ItemAPI, not nested containers)
   */
  const getItemsForContainer = useCallback((containerId: string): ItemAPI[] => {
    const { element: container } = getContainer(containerId);
    if (!container?.content?.items) return [];
    return container.content.items.filter((item): item is ItemAPI => isItemAPI(item));
  }, [getContainer]);

  /**
   * Handle drag start
   */
  const handleDragStart = useCallback((initial: DragStart, provided: ResponderProvided) => {
    const { source, draggableId } = initial;

    setDragState({
      isDragging: true,
      sourceDroppableId: source.droppableId,
      sourceIndex: source.index,
      draggableId,
      draggedItemType: 'item', // For now, all drags within canvas are items
    });

    // Show initial drop indicator at source position
    setDropIndicator({
      containerId: source.droppableId,
      index: source.index,
      isVisible: true,
      position: null,
    });

    // Highlight source container
    setContainerHighlight({
      containerId: source.droppableId,
      isActive: true,
    });
  }, []);

  /**
   * Handle drag update - update drop indicator position
   */
  const handleDragUpdate = useCallback((update: DragUpdate, provided: ResponderProvided) => {
    const { destination } = update;

    if (!destination) {
      // No valid drop target
      setDropIndicator(prev => ({
        ...prev,
        isVisible: false,
        position: null,
      }));
      setContainerHighlight({
        containerId: null,
        isActive: false,
      });
      return;
    }

    // Update container highlight
    setContainerHighlight({
      containerId: destination.droppableId,
      isActive: true,
    });

    // Update drop indicator to new position
    setDropIndicator({
      containerId: destination.droppableId,
      index: destination.index,
      isVisible: true,
      position: null, // Position calculated by the component if needed
    });
  }, []);

  /**
   * Handle drag end - dispatch appropriate command
   */
  const handleDragEnd = useCallback((result: DropResult, provided: ResponderProvided) => {
    // Reset drag state
    setDragState({
      isDragging: false,
      sourceDroppableId: null,
      sourceIndex: 0,
      draggableId: null,
      draggedItemType: null,
    });

    // Hide drop indicator
    setDropIndicator({
      containerId: null,
      index: 0,
      isVisible: false,
      position: null,
    });

    // Clear container highlight
    setContainerHighlight({
      containerId: null,
      isActive: false,
    });

    const { source, destination, draggableId } = result;

    // If no destination, drop was cancelled
    if (!destination) {
      return;
    }

    // If dropped in same position, no action needed
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    // Get canvas IDs
    const fromCanvasId = getCanvasIdForContainer(source.droppableId);
    const toCanvasId = getCanvasIdForContainer(destination.droppableId);

    if (!fromCanvasId) {
      console.warn('Could not determine source canvas ID');
      return;
    }

    // Case 1: Same container reorder
    if (source.droppableId === destination.droppableId) {
      const containerId = source.droppableId;
      const items = getItemsForContainer(containerId);

      if (items.length === 0) return;

      // Create new order array
      const itemIds = items.map(item => item.id);
      const [movedId] = itemIds.splice(source.index, 1);
      itemIds.splice(destination.index, 0, movedId);

      const command = createReorderItemsCommand(
        {
          canvasId: fromCanvasId,
          containerId,
          itemIds,
        },
        `Reorder items in container`
      );

      dispatch({ type: 'EXECUTE_COMMAND', payload: command });

      return;
    }

    // Case 2: Cross-container move
    if (!toCanvasId) {
      console.warn('Could not determine target canvas ID');
      return;
    }

    // Free style lifts the submodel movement restrictions below — items move anywhere.
    if (!selectors.isFreestyle(state.model)) {
      // Rule 3: Items can only move within the same root container hierarchy
      const rootFrom = selectors.selectRootContainer(state, source.droppableId);
      const rootTo = selectors.selectRootContainer(state, destination.droppableId);
      if (!rootFrom || !rootTo || rootFrom.id !== rootTo.id) {
        // Block: different root hierarchies — silently cancel
        return;
      }

      // Submodel lock: block moving an item into a container locked to a DIFFERENT submodel
      // (also enforces the max>1 cascade + max propagation). Empty/unlocked targets are allowed.
      const movedItem = getItemsForContainer(source.droppableId).find((i) => i.id === draggableId);
      if (movedItem?.parentId) {
        const toContainers = state.model?.layout.canvas[toCanvasId]?.containers || [];
        const lock = canAddSubmodelItemToContainer(
          toContainers,
          destination.droppableId,
          movedItem.parentId,
          (smId) => getSubmodelInstanceLimits(smId).maxInstantiation
        );
        if (!lock.allowed) {
          toast.error(lock.message || 'Cannot move this item here.');
          return;
        }
      }
    }

    const command = createMoveItemCommand(
      {
        itemId: draggableId,
        fromCanvasId,
        toCanvasId,
        fromContainerId: source.droppableId,
        toContainerId: destination.droppableId,
        fromIndex: source.index,
        toIndex: destination.index,
      },
      `Move item to different container`
    );

    dispatch({ type: 'EXECUTE_COMMAND', payload: command });
  }, [state, dispatch, getCanvasIdForContainer, getItemsForContainer, getSubmodelInstanceLimits]);

  const contextValue: DragDropContextValue = {
    dragState,
    dropIndicator,
    setDropIndicator,
    containerHighlight,
    setContainerHighlight,
  };

  return (
    <DragDropContextInternal.Provider value={contextValue}>
      <RBDDragDropContext
        onDragStart={handleDragStart}
        onDragUpdate={handleDragUpdate}
        onDragEnd={handleDragEnd}
      >
        {children}
      </RBDDragDropContext>
    </DragDropContextInternal.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

export const useDragDropContext = (): DragDropContextValue => {
  const context = useContext(DragDropContextInternal);
  if (!context) {
    throw new Error('useDragDropContext must be used within a DragDropProvider');
  }
  return context;
};

/**
 * Optional hook that returns null if used outside provider
 * (useful for components that may or may not be in drag-drop context)
 */
export const useDragDropContextOptional = (): DragDropContextValue | null => {
  return useContext(DragDropContextInternal);
};

export default DragDropProvider;
